import React from 'react';
import type { ForecastDay } from '../../types/weather';
import type { WeatherLoadStatus } from '../../services/weatherOrchestrator';
import type { TemperatureUnit } from '../../utils/temperatureUnit';
import { formatTempRange } from '../../utils/temperatureUnit';
import { weatherEn } from '../../content/weather.en';
import WeatherConditionIcon from './WeatherConditionIcon';
import styles from './DayWeatherSummary.module.css';

export interface DayWeatherSummaryProps {
  day: ForecastDay | null;
  unit: TemperatureUnit;
  status: WeatherLoadStatus;
  showHistoricalBadge?: boolean;
}

const DayWeatherSummary: React.FC<DayWeatherSummaryProps> = ({
  day,
  unit,
  status,
  showHistoricalBadge = false,
}) => {
  if (status === 'loading') {
    return <p className={styles.loading}>{weatherEn.loading}</p>;
  }

  if (status === 'unavailable') {
    return <p className={styles.unavailable}>{weatherEn.weatherUnavailable}</p>;
  }

  if (status === 'error' || !day) {
    return <p className={styles.unavailable}>{weatherEn.unavailable}</p>;
  }

  return (
    <div className={styles.summary} data-testid="day-weather-summary">
      <div className={styles.mainRow}>
        <WeatherConditionIcon
          iconName={day.iconName}
          dimmed={day.isHistorical}
          size={22}
          className={styles.icon}
        />
        <div className={styles.details}>
          <div className={styles.temps}>
            <span className={styles.highLow}>
              {formatTempRange(day.highTempC, day.lowTempC, unit)}
            </span>
            <span className={styles.condition}>{day.condition}</span>
          </div>
          {day.precipitationProbability > 0 ? (
            <div className={styles.precip}>
              {weatherEn.precip}: {Math.round(day.precipitationProbability)}%
            </div>
          ) : null}
        </div>
      </div>
      {showHistoricalBadge && day.isHistorical ? (
        <div className={styles.historicalBadge}>{weatherEn.seasonalHistorical}</div>
      ) : null}
    </div>
  );
};

export default DayWeatherSummary;
